import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

// Salon bilgileri için prop type tanımı
export interface HeaderProps {
  salonInfo: {
    name: string;
    logo?: string;
    address: string;
    city: string;
    district: string;
  };
}

const Header: React.FC<HeaderProps> = ({ salonInfo }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  
  // Ana sayfada mıyız?
  const isHomePage = location.pathname === `/${slug}` || location.pathname === `/${slug}/`;

  // Giriş kısmında mıyız (ana sayfada ve scroll edilmemiş)
  const isIntroSection = isHomePage && !isScrolled;

  // Logo URL'sini al ve formatla
  const getLogoUrl = (logoPath: string | undefined): string => {
    if (!logoPath) return '';
    
    // Eğer logo tam URL ise (http veya https ile başlıyorsa) doğrudan kullan
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    
    // API'nin yüklediği logoları için base URL ekle
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.345.tr';
    console.log('Logo path:', logoPath);
    
    // URL'de /v1 yolunu kontrol et ve yoksa ekle
    if (logoPath.startsWith('/uploads') && !baseUrl.endsWith('/v1')) {
      return `${baseUrl}/v1${logoPath}`;
    }
    
    return `${baseUrl}${logoPath}`;
  };

  const handleLogoError = () => {
    console.error('Logo yüklenemedi:', salonInfo.logo);
    setLogoError(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <HeaderContainer scrolled={isScrolled}>
      <div className="container">
        <HeaderContent>
          <Logo to={`/${slug}`}>
            {salonInfo.logo && !logoError ? (
              <LogoImage 
                src={getLogoUrl(salonInfo.logo)} 
                alt={`${salonInfo.name} logo`} 
                onError={handleLogoError}
              />
            ) : (
              <LogoText>{salonInfo.name}</LogoText>
            )}
          </Logo>

          <DesktopNav>
            <NavList>
              <NavItem active={isHomePage}>
                <StyledNavLink to={`/${slug}`} isIntroSection={isIntroSection}>Ana Sayfa</StyledNavLink>
              </NavItem>
              <NavItem active={location.pathname.includes('/hizmetler')}>
                <StyledNavLink to={`/${slug}/hizmetler`} isIntroSection={isIntroSection}>Hizmetlerimiz</StyledNavLink>
              </NavItem>
              <NavItem active={location.pathname.includes('/galeri')}>
                <StyledNavLink to={`/${slug}/galeri`} isIntroSection={isIntroSection}>Galeri</StyledNavLink>
              </NavItem>
              <NavItem active={location.pathname.includes('/iletisim')}>
                <StyledNavLink to={`/${slug}/iletisim`} isIntroSection={isIntroSection}>İletişim</StyledNavLink>
              </NavItem>
            </NavList>
          </DesktopNav>

          <StyledAppointmentButton to={`/${slug}/randevu`} isIntroSection={isIntroSection}>
            Randevu Al
          </StyledAppointmentButton>

          <MobileMenuToggle onClick={toggleMobileMenu} isIntroSection={isIntroSection}>
            <span></span>
            <span></span>
            <span></span>
          </MobileMenuToggle>
        </HeaderContent>
      </div>

      <MobileNav open={mobileMenuOpen}>
        <MobileNavList>
          <MobileNavItem active={isHomePage}>
            <MobileNavLink to={`/${slug}`} onClick={closeMobileMenu}>
              Ana Sayfa
            </MobileNavLink>
          </MobileNavItem>
          <MobileNavItem active={location.pathname.includes('/hizmetler')}>
            <MobileNavLink to={`/${slug}/hizmetler`} onClick={closeMobileMenu}>
              Hizmetlerimiz
            </MobileNavLink>
          </MobileNavItem>
          <MobileNavItem active={location.pathname.includes('/galeri')}>
            <MobileNavLink to={`/${slug}/galeri`} onClick={closeMobileMenu}>
              Galeri
            </MobileNavLink>
          </MobileNavItem>
          <MobileNavItem active={location.pathname.includes('/iletisim')}>
            <MobileNavLink to={`/${slug}/iletisim`} onClick={closeMobileMenu}>
              İletişim
            </MobileNavLink>
          </MobileNavItem>
          <MobileNavItem>
            <MobileAppointmentButton to={`/${slug}/randevu`} onClick={closeMobileMenu}>
              Randevu Al
            </MobileAppointmentButton>
          </MobileNavItem>
        </MobileNavList>
      </MobileNav>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${({ scrolled, theme }) =>
    scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  backdrop-filter: ${({ scrolled }) =>
    scrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${({ scrolled, theme }) =>
    scrolled ? '0 10px 30px rgba(0, 0, 0, 0.08)' : 'none'};
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  padding: ${({ scrolled }) => (scrolled ? '12px 0' : '25px 0')};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
`;

const Logo = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  transform: translateY(0);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const LogoText = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: 'Montserrat', sans-serif;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1.2rem;
  }
`;

const LogoImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  background-color: transparent;
  border-radius: 50%;
`;

const DesktopNav = styled.nav`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 32px;
`;

const NavItem = styled.li<{ active: boolean }>`
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: ${({ active }) => (active ? '100%' : '0')};
    height: 2px;
    background: ${({ theme }) => theme.colors.primary};
    transition: width 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    border-radius: 2px;
  }
  
  &:hover:after {
    width: 100%;
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const StyledNavLink = styled(Link)<{ isIntroSection: boolean }>`
  color: ${({ isIntroSection, theme }) => isIntroSection ? 'white' : theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  letter-spacing: 0.6px;
  transition: all 0.3s;
  position: relative;
  padding: 5px 2px;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const StyledAppointmentButton = styled(Link)<{ isIntroSection: boolean }>`
  background-color: transparent;
  color: ${({ isIntroSection, theme }) => isIntroSection ? 'white' : theme.colors.text};
  padding: 12px 28px;
  position: relative;
  border-radius: 50px;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 0.95rem;
  letter-spacing: 0.8px;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: 1;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50px;
    padding: 2px;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    z-index: -2;
    border-radius: 50px;
  }
  
  &:hover {
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0, 157, 255, 0.25);
    
    &:after {
      left: 0;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuToggle = styled.div<{ isIntroSection: boolean }>`
  display: none;
  flex-direction: column;
  cursor: pointer;
  
  span {
    height: 2px;
    width: 26px;
    background: ${({ isIntroSection, theme }) => isIntroSection ? 'white' : theme.colors.text};
    margin-bottom: 5px;
    border-radius: 5px;
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    
    &:last-child {
      width: 18px;
      margin-left: auto;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

const MobileNav = styled.nav<{ open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 300px;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: ${({ theme }) => theme.shadows.large};
  padding: 80px 20px 20px;
  transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease;
  z-index: 999;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MobileNavItem = styled.li<{ active?: boolean }>`
  margin-bottom: 20px;
  border-left: 3px solid ${({ active, theme }) =>
    active ? theme.colors.primary : 'transparent'};
  padding-left: 10px;
`;

const MobileNavLink = styled(Link)`
  display: block;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  font-size: 1.1rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  padding: 5px 0;
`;

const MobileAppointmentButton = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.primary};
  padding: 10px 22px;
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 0.95rem;
  margin-top: 10px;
  transition: all 0.3s;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    padding: 2px;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

export default Header;